import {
  CalendarDate,
  endOfMonth,
  getLocalTimeZone,
  startOfMonth,
  today,
} from "@internationalized/date";
import DateRangePicker from "components/datePickers/DateRangePicker";
import PageHeader from "components/lib/pageHeader";
import { PsychologistDropdown } from "components/lib/PsychologistDropdown";
import { getUnixTime } from "date-fns";
import { useSelectedPsychologistIdParam } from "hooks/useSelectedPsychologistParam";
import { useUserHasRole } from "hooks/userHooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { renderWeeks } from "./dashboard.utils";
import { CalendarContainer, Container } from "./styled";
import { renderDataRow } from "./toolbar/toolbar.utils";
import { useOverviewData } from "./useOverviewData";

export const Dashboard = () => {
  const { t } = useTranslation();
  const [dropdownSelectedPsychologistId] = useSelectedPsychologistIdParam();

  const [dateRangePickerValue, setDateRangePickerValue] = useState<{
    start: CalendarDate;
    end: CalendarDate;
  }>({
    start: startOfMonth(today(getLocalTimeZone())),
    end: endOfMonth(today(getLocalTimeZone())),
  });
  const isAdmin = useUserHasRole("Admin");
  const startDateUnix = getUnixTime(
    dateRangePickerValue.start.toDate(getLocalTimeZone()),
  );
  const endDateUnix = getUnixTime(
    dateRangePickerValue.end.toDate(getLocalTimeZone()),
  );

  const { data } = useOverviewData({
    psychologistId: dropdownSelectedPsychologistId,
    startDateUnix,
    endDateUnix,
  });

  return (
    <Container>
      <ToolbarContainer>
        <InfoContainer>
          <PageHeader
            translation={t("DASHBOARD")}
            mb={4}
            hasSensitive={false}
          />
          {isAdmin && <PsychologistDropdown />}
          <SelectionWrapper>
            <DateRangePicker
              label={"Date range"}
              value={dateRangePickerValue}
              onChange={(val) => {
                if (val) {
                  setDateRangePickerValue(val);
                }
              }}
              granularity="day"
              shouldForceLeadingZeros
            />
          </SelectionWrapper>
        </InfoContainer>
        <StatsContainer>
          {renderDataRow({
            dateFrom: startDateUnix,
            dateTo: endDateUnix,
            overviewData: data,
          })}
        </StatsContainer>
      </ToolbarContainer>
      <div>
        <CalendarContainer>
          <p />
          <p>{t("CALENDAR_MONDAY")}</p>
          <p>{t("CALENDAR_TUESDAY")}</p>
          <p>{t("CALENDAR_WEDNESDAY")}</p>
          <p>{t("CALENDAR_THURSDAY")}</p>
          <p>{t("CALENDAR_FRIDAY")}</p>
          <p>{t("CALENDAR_SATURDAY")}</p>
          <p>{t("CALENDAR_SUNDAY")}</p>
        </CalendarContainer>
        {renderWeeks(data)}
      </div>
    </Container>
  );
};

const ToolbarContainer = styled.div`
  width: 1129px;
  display: flex;
  align-items: flex-start;
  margin-bottom: 32px;
  padding-right: 32px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 50px;
  gap: 16px;
`;

const SelectionWrapper = styled.div`
  margin-bottom: 18px;
`;

const StatsContainer = styled.div`
  width: 450px;
  padding: 20px 24px 12px;
  margin-left: auto;
  margin-top: 32px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
`;
